<script setup lang="ts">
const { t, locale } = useI18n();
const localePath = useLocalePath();
const slug = useRoute().params.slug as string;
const stemPrefix = locale.value === "fr" ? "alternatives/" : `${locale.value}/alternatives/`;
const { data: page } = await useAsyncData(`alternative-${locale.value}-${slug}`, () =>
  queryCollection("alternatives")
    .where("stem", "=", stemPrefix + slug)
    .first()
);

if (!page?.value) {
  throw createError({
    statusCode: 404,
    message: "Page introuvable",
  });
}

if (page.value.seo) {
  useSeoMeta({
    title: page.value.seo.title,
    description: page.value.seo.description,
  });

   import.meta.prerender && defineOgImageComponent("Default", {
    title: page.value.seo.title,
    description: page.value.seo.description,
  });
}

useSchemaOrg([defineArticle()]);

// Active TOC item tracking
const activeId = ref<string>("");

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          activeId.value = entry.target.id;
        }
      });
    },
    {
      rootMargin: "-100px 0px -66%",
      threshold: 0,
    }
  );

  // Observe all headings
  document.querySelectorAll("h2[id], h3[id]").forEach((heading) => {
    observer.observe(heading);
  });

  onUnmounted(() => observer.disconnect());
});

const scrollToHeading = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    const offset = 100;
    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: elementPosition - offset,
      behavior: "smooth",
    });
  }
};
</script>

<template>
  <main class="relative">
    <!-- Background gradient -->
    <div
      class="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
      aria-hidden="true"
    >
      <div
        class="to-primary-300 relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-blue-300 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
        style="
          clip-path: polygon(
            74.1% 44.1%,
            100% 61.6%,
            97.5% 26.9%,
            85.5% 0.1%,
            80.7% 2%,
            72.5% 32.5%,
            60.2% 62.4%,
            52.4% 68.1%,
            47.5% 58.3%,
            45.2% 34.5%,
            27.5% 76.7%,
            0.1% 64.9%,
            17.9% 100%,
            27.6% 76.8%,
            76.1% 97.7%,
            74.1% 44.1%
          );
        "
      />
    </div>

    <!-- Main content area -->
    <div class="mx-auto max-w-7xl px-6 pt-10 lg:px-8 lg:pt-40">
      <div class="lg:flex lg:gap-12 xl:gap-16">
        <!-- Main content -->
        <article class="min-w-0 flex-1">
          <ContentRenderer
            v-if="page"
            class="prose max-w-none prose-h1:mt-20 prose-h1:font-serif prose-a:prose-headings:no-underline prose-h1:sm:mt-12"
            :value="page"
          />

          <!-- Back to hub link -->
          <div class="mt-12 border-t border-gray-200 pb-24 pt-8">
            <NuxtLinkLocale
              to="/alternative"
              class="text-primary-600 inline-flex items-center gap-2 hover:text-primary-500"
            >
              <Icon name="heroicons:arrow-left" class="size-4" />
              {{ t('alternatives.viewAllComparisons') }}
            </NuxtLinkLocale>
          </div>
        </article>

        <!-- TOC Sidebar - Sticky -->
        <aside
          v-if="page?.body?.toc?.links?.length"
          class="hidden w-56 shrink-0 lg:block xl:w-64"
        >
          <div class="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto">
            <nav>
              <p class="mb-4 text-sm font-semibold text-gray-900">
                {{ t('alternatives.onThisPage') }}
              </p>
              <ul class="space-y-2.5 text-sm">
                <li
                  v-for="link in page.body.toc.links"
                  :key="link.id"
                >
                  <button
                    type="button"
                    class="block w-full text-left transition-colors duration-150"
                    :class="[
                      activeId === link.id
                        ? 'text-primary-600 font-medium'
                        : 'text-gray-600 hover:text-gray-900',
                    ]"
                    @click="scrollToHeading(link.id)"
                  >
                    {{ link.text }}
                  </button>
                  <!-- Nested links (H3) -->
                  <ul
                    v-if="link.children?.length"
                    class="mt-2 space-y-2 border-l border-gray-200 pl-3"
                  >
                    <li
                      v-for="child in link.children"
                      :key="child.id"
                    >
                      <button
                        type="button"
                        class="block w-full text-left text-[13px] transition-colors duration-150"
                        :class="[
                          activeId === child.id
                            ? 'text-primary-600 font-medium'
                            : 'text-gray-500 hover:text-gray-700',
                        ]"
                        @click="scrollToHeading(child.id)"
                      >
                        {{ child.text }}
                      </button>
                    </li>
                  </ul>
                </li>
              </ul>
            </nav>

            <!-- Quick CTA -->
            <div class="border-primary-200 from-primary-50 mt-8 rounded-xl border bg-gradient-to-br to-white p-4">
              <p class="text-sm font-semibold text-gray-900">
                {{ t('alternatives.readyToTry') }}
              </p>
              <p class="mt-1 text-xs text-gray-600">
                {{ t('alternatives.createFirstContract') }}
              </p>
              <UButton
                :to="localePath('/app')"
                size="sm"
                class="mt-3 w-full justify-center"
              >
                {{ t('alternatives.startFree') }}
              </UButton>
            </div>
          </div>
        </aside>
      </div>
    </div>

    <LandingEndCta />
  </main>
</template>
